import { EditorCardProvider } from '@atlaskit/editor-card-provider';
import { EmojiResource } from '@atlaskit/emoji/resource';
import { prefetchNodeDataProvidersData } from '@atlaskit/node-data-provider';
import { CardClient } from '@atlaskit/link-provider';

import {
	type SessionDataType,
	getSessionData as uninstrumentedGetSessionData,
} from '@confluence/session-data';
import { createEmojiProviderParameters } from '@confluence/fabric-emoji-support';
import { getEmojiNodeDataProvider } from '@confluence/fabric-providers/entry-points/EmojiProvider';
import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures';
import { createCookie } from '@confluence/content-smartlinks';
import { expVal } from '@confluence/feature-experiments';
import { query } from '@confluence/query-preloader-tools';
import { getSSRRenderInputs } from '@confluence/ssr-utilities';

import {
	NODE_DATA_PROVIDERS_MAX_NODES_TO_PREFETCH,
	NODE_DATA_PROVIDERS_MAX_NODES_TO_VISIT,
} from './utils/nodeDataProvidersConstants';
import type {
	AdfDocQuery as AdfDocQueryRes,
	AdfDocQueryVariables,
} from './queries/__types__/AdfDocQuery';
import { AdfDocQuery } from './queries/AdfDocQuery.graphql';

const MEASURE_SEGMENT = 'ssr-app/prepare/preloadQuery/editor/nodeDataProvidersData';
const DEFAULT_TIMEOUT = 500;

export async function preloadEditorNodeDataProvidersData(
	contentId: string | undefined | null,
): Promise<void> {
	if (!contentId) {
		return;
	}

	if (!isSSR()) {
		// If we are not in SSR, we don't need to preload node data providers data.
		return;
	}

	await SSRMeasures.run(MEASURE_SEGMENT, async () => {
		const fetchAdfStart = performance.now();
		const docJsonString = await fetchAdf(contentId);
		const fetchAdfDuration = performance.now() - fetchAdfStart;

		if (!docJsonString) {
			return;
		}

		const doc = JSON.parse(docJsonString);

		// Should be removed when all experiments are removed
		const experimentTimeouts: number[] = [];
		const providers: Parameters<typeof prefetchNodeDataProvidersData>[0]['providers'] = [];

		if (expVal('platform_editor_smart_card_otp', 'isEnabled', false)) {
			providers.push({
				provider: ssrEditorCardProvider(),
				maxNodesToPrefetch: NODE_DATA_PROVIDERS_MAX_NODES_TO_PREFETCH.smartCard,
			});
			experimentTimeouts.push(
				Math.max(
					0,
					expVal('platform_editor_smart_card_otp', 'timeoutStrategy', 'total') === 'total'
						? Math.round(
								expVal('platform_editor_smart_card_otp', 'timeout', 500) - fetchAdfDuration,
							)
						: expVal('platform_editor_smart_card_otp', 'timeout', 500),
				),
			);
		}
		if (expVal('platform_editor_emoji_otp', 'isEnabled', false)) {
			const sessionData = await getSessionData();
			const provider = ssrEmojiNodeDataProvider(sessionData);

			if (provider) {
				providers.push({
					provider,
					maxNodesToPrefetch: NODE_DATA_PROVIDERS_MAX_NODES_TO_PREFETCH.emoji,
				});
				experimentTimeouts.push(expVal('platform_editor_emoji_otp', 'timeout', 500));
			}
		}

		providers.forEach(({ provider }) => {
			SSRMeasures.markMeasureStart(measureProviderSegment(provider.name));
		});

		const editorNodeDataProvidersData = await prefetchNodeDataProvidersData({
			doc,
			providers,
			timeout: experimentTimeouts.length > 0 ? Math.min(...experimentTimeouts) : DEFAULT_TIMEOUT,
			maxNodesToVisit: NODE_DATA_PROVIDERS_MAX_NODES_TO_VISIT,
		});

		Object.entries(editorNodeDataProvidersData).forEach(([providerName, { duration }]) => {
			SSRMeasures.setMeasureDuration(measureProviderSegment(providerName), duration);
		});

		window['__SSR_EDITOR_NODE_DATA_PROVIDERS_DATA__'] = Object.fromEntries(
			Object.entries(editorNodeDataProvidersData).map(([providerName, { data }]) => [
				providerName,
				data,
			]),
		);
	});
}

function fetchAdf(contentId: string | undefined | null): Promise<string | undefined> {
	return SSRMeasures.run(`${MEASURE_SEGMENT}/fetch:AdfDocQuery`, async () => {
		try {
			const result = await query<AdfDocQueryRes, AdfDocQueryVariables>({
				query: AdfDocQuery,
				variables: {
					contentId,
					status: ['draft'],
				},
				fetchPolicy: 'network-only',
			});

			if (result.hasErrors) {
				return undefined;
			}

			return result.data?.singleContent?.body?.atlas_doc_format?.value || undefined;
		} catch {
			return undefined;
		}
	});
}

function getSessionData(): Promise<SessionDataType> {
	return SSRMeasures.run(`${MEASURE_SEGMENT}/getSessionData`, async () => {
		return uninstrumentedGetSessionData();
	});
}

function ssrEmojiNodeDataProvider(sessionData: SessionDataType) {
	const { userId, cloudId, isLicensed, isLoggedIn } = sessionData;

	// We need a special emoji parameters that supports SSR load optimization here.
	const emojiResourceParams = createEmojiProviderParameters({
		cloudId,
		userId,
		isLicensed,
		isLoggedIn,
	});

	return getEmojiNodeDataProvider(EmojiResource, {
		...emojiResourceParams,
		allowAnonymousAccess: true,
	});
}

function ssrEditorCardProvider() {
	const { host, cloudId, userContext, customURLs } = getSSRRenderInputs();

	// This URL will be something like `https://object-resolver.staging.atl-paas.net/resolve/batch`.
	// We need only protocol and host to pass it to the EditorCardProvider,
	// because path will be added by `CardClient`.
	const objectResolverURL =
		customURLs?.['object-resolver-service']?.match(/^(https?:\/\/[^/]+)/)?.[0];

	// We need to define a custom CardClient for SSR, because the default one works through public API gateway.
	const ssrCardClient = new CardClient('custom', objectResolverURL);
	// Headers from `fetchORSForSSR` from confluence/next/packages/content-smartlinks/src/index.ts
	ssrCardClient.setHeaders({
		Cookie: createCookie(userContext),
		Origin: host.startsWith('http') ? host : `https://${host}`,
		CloudId: cloudId,
	});

	return new EditorCardProvider(
		'custom',
		objectResolverURL,
		'CONFLUENCE',
		undefined,
		ssrCardClient,
	);
}

function isSSR(): boolean {
	try {
		return Boolean(
			// In most places there is no document when running on server-side
			typeof document === 'undefined' ||
				// When SSRing editor with full cycle mode we define the document
				// Check Confluence specific environment variable
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				(typeof process !== 'undefined' && process.env.REACT_SSR),
		);
	} catch (e) {
		// Catch possible error that might occur and just return false
		return false;
	}
}

function measureProviderSegment(providerName: string) {
	return `${MEASURE_SEGMENT}/${providerName}`;
}
